'use client';

import * as React from 'react';
import { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { PortableText } from 'next-sanity';
import { PortableTextBlock } from 'sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GROQButton } from '@/sanity/lib/definitions';
import { CTAButton } from '@/shadcn/ui/button';
import { CTAWrapper } from '@/components/global/utils/CTAWrapper';
import LogoIcon from '@/components/global/LogoIcon';
import CountdownBig from '@/components/pageComponents/global/CountdownBig';
import { verifyGatePassword, GateState } from '@/app/actions/gate';
import PageSalesPageJSX from '@/components/pages/PageSalesPageJSX';
import { PageSalesPageSchema } from '@/sanity/pages/PageSalesPageTemplate';
import heroBackground from '@/public/sales-page/heroBlurred.png';
import { useIsMobile } from '@/hooks/useIsMobile';
import Countdown from '@/components/pageComponents/global/Countdown';

interface IPasswordGate {
  docId: string;
  heroStickyButton?: {
    heroStickyButton1?: GROQButton;
    heroStickyButton2?: GROQButton;
  };
  textPreTitle?: {
    firstText?: string;
    secondText?: string;
    secondTextBold?: string;
  };
  countdownTime?: string;
  label?: string;
  title?: PortableTextBlock[];
  submitLabel?: string;
  errorMessage?: string;
  waitlistText?: string;
  waitlistButton?: GROQButton;
}

const initialState: GateState = { ok: false };

// Runtime is React 19 (which has useActionState) but @types/react is still on 18,
// so reach for it through a typed cast until the types are upgraded.
const useActionState = (
  React as unknown as {
    useActionState: <S>(
      action: (state: S, payload: FormData) => S | Promise<S>,
      initialState: S,
    ) => [S, (payload: FormData) => void, boolean];
  }
).useActionState;

function Sparkle({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 31 17" fill="none" aria-hidden>
      <path
        d="M24.7267 8.63806L30.3864 8.7828L30.3929 7.59186L24.732 7.67072C21.0754 7.65923 18.0794 4.25772 17.8905 0.00746362L16.9009 -1.82991e-05C16.6841 4.25528 13.6632 7.61517 10.0066 7.60368L0.00652463 7.39383L-2.50672e-05 8.5848L10.0013 8.57105C13.6579 8.58254 16.6539 11.9841 16.8428 16.2343L17.8324 16.2418C18.0492 11.9865 21.0702 8.62657 24.7267 8.63806Z"
        fill="#DADADA"
      />
    </svg>
  );
}

/**
 * Server-gated lock screen. The page route always renders this (instead of the
 * content) for gated pages, so none of the protected sections are ever sent to
 * the client up front. The password is validated by a server action which, on
 * success, returns the page data — rendered here in memory for this render only.
 * Nothing is persisted, so reloading the page requires the password again.
 */
export default function PasswordGate({
  docId,
  heroStickyButton,
  textPreTitle,
  countdownTime,
  label,
  title,
  submitLabel = 'Access now',
  errorMessage = 'Incorrect password. Please try again.',
  waitlistText,
  waitlistButton,
}: IPasswordGate) {
  const [state, formAction, pending] = useActionState(
    verifyGatePassword.bind(null, docId),
    initialState,
  );
  const isMobile = useIsMobile();

  // On a correct password the server action returns the page data. We render it
  // here, in memory, for this render only — nothing is persisted, so a reload
  // starts over and the password is required again.
  const revealed = Boolean(state.ok && state.data);

  // Lock scrolling while gated — including defeating manual scroll changes from
  // dev tools (the scroll listener snaps the page back to the top). Once the
  // content is revealed, the page scrolls normally again.
  useEffect(() => {
    if (revealed) return;

    const { body } = document;
    const html = document.documentElement;
    const prevBody = body.style.overflow;
    const prevHtml = html.style.overflow;
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    const snapToTop = () => window.scrollTo(0, 0);
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
      ) {
        return;
      }
      const blocked = [
        'ArrowUp',
        'ArrowDown',
        'PageUp',
        'PageDown',
        'Home',
        'End',
        ' ',
        'Spacebar',
      ];
      if (blocked.includes(e.key)) e.preventDefault();
    };

    window.scrollTo(0, 0);
    window.addEventListener('scroll', snapToTop, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
      window.removeEventListener('scroll', snapToTop);
      window.removeEventListener('keydown', onKey);
    };
  }, [revealed]);

  // Password accepted → render the real page with the data returned by the
  // server action (this render only — not persisted).
  if (revealed) {
    return <PageSalesPageJSX data={state.data as PageSalesPageSchema} />;
  }

  return (
    <main className="fixed inset-0 overflow-hidden bg-black">
      {/* Blurred decorative background — protected content is never rendered. */}
      <div className="absolute inset-0 -z-10 select-none pointer-events-none">
        <Image
          src={heroBackground}
          alt=""
          fill
          priority
          className="object-cover blur-[1rem]"
        />
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Testeira (sharp header) */}
      <div className="absolute top-0 left-0 w-full lg:h-[8.125rem] flex items-center lg:bg-black/75 lg:backdrop-blur-[1rem] z-20">
        <div className="w-full px-[2rem] lg:px-[9rem] flex flex-col gap-[5.18rem] lg:gap-0 justify-center lg:grid grid-cols-3 items-center pt-[3rem] lg:pt-0">
          <div className="flex flex-row items-center gap-[1rem] font-nunito text-[#F2F0EF] text-[2rem] font-[200] prose-bold:font-[600] leading-[100%] px-[2rem] lg:px-0">
            {textPreTitle?.firstText && (
              <p><b>{textPreTitle.firstText}</b></p>
            )}
            <span className="w-[4rem] h-[0.125rem] bg-[#898989]" />
            {(textPreTitle?.secondText || textPreTitle?.secondTextBold) && (
              <p>{textPreTitle?.secondText} <b>{textPreTitle?.secondTextBold}</b></p>
            )}
          </div>
          <Link className="w-fit lg:mx-auto hidden lg:block" href="/" aria-label="Veylor">
            <LogoIcon
              fill="#F2F0EF"
              className="w-[5.875rem] lg:w-[4.67338rem] h-auto aspect-[47/40]"
            />
          </Link>
          <div className="hidden lg:flex gap-[2rem] items-center justify-end">
            {heroStickyButton?.heroStickyButton1 && (
              <CTAButton
                className="[&_button]:!py-[2rem] [&_button]:!px-[2.5rem]"
                data={heroStickyButton.heroStickyButton1}
              />
            )}
            {heroStickyButton?.heroStickyButton2 && (
              <CTAButton
                className="[&_button]:!min-h-0 [&_button]:!py-[2rem] [&_button]:!bg-transparent [&_button]:!backdrop-blur-0"
                data={heroStickyButton.heroStickyButton2}
              />
            )}
          </div>
        </div>
      </div>

      {/* Popup */}
      <div className="absolute inset-0 z-10 overflow-y-auto flex items-center justify-center px-[2rem] py-[16rem] lg:py-[12rem]">
        <div className="flex flex-col items-center gap-[3rem] px-[2rem] py-[3rem] lg:p-[6rem] backdrop-blur-md bg-white/10 lg:bg-white/[15%] rounded-[1.32213rem] border border-white/[12%]">
          {countdownTime && (
            <div className="flex flex-col items-center">
              {isMobile ? <Countdown date={countdownTime} enableDays enableStrings /> : <CountdownBig date={countdownTime} enableDays enableStrings />}
            </div>
          )}

          <div className="w-full flex flex-col gap-[3rem] rounded-[1.25rem] border border-white/[12%] bg-[#262626] backdrop-blur-[1rem] px-[3rem] py-[5rem] lg:p-[4rem]">
            {label && (
              <div className="flex flex-col lg:flex-row items-center gap-[5rem] lg:gap-[2rem]">
                <svg className="w-[5.875rem] lg:w-[2.875rem] h-auto lg:mb-[.25rem]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 20" fill="none">
                  <path d="M23 6.21031L22.8583 6.05719C22.1101 6.25985 20.7553 6.53456 19.0862 6.48503L18.9578 6.61563C19.281 7.39923 19.7061 9.05202 18.1432 10.1103C16.7885 11.1687 15.2123 11.0651 14.0258 9.83562C12.2947 8.03873 11.5376 5.12497 12.5249 4.06215C12.9367 3.60279 13.4458 3.3596 13.8398 3.23351V3.05337C12.6666 1.89597 11.9627 0.711552 11.6085 0H11.3915C11.0329 0.711552 10.329 1.90047 9.16015 3.05337V3.23351C9.55418 3.3596 10.0633 3.60279 10.4751 4.06215C11.4624 5.12497 10.7053 8.03873 8.9742 9.83562C7.79211 11.0651 6.21598 11.1732 4.85679 10.1103C3.29836 9.05202 3.71896 7.39923 4.04215 6.61563L3.91376 6.48503C2.24466 6.53456 0.889894 6.25985 0.141675 6.05719L0 6.21031C0.256785 6.94889 0.606544 8.27742 0.548988 9.9392L0.677382 10.0698C2.10298 9.48886 3.13455 10.0563 3.99788 10.6417C7.90722 13.2898 10.329 16.3567 11.3871 20H11.604C12.6622 16.3567 15.0839 13.2898 18.9933 10.6417C19.8566 10.0563 20.8926 9.48886 22.3138 10.0698L22.4422 9.9392C22.3846 8.28192 22.7344 6.94889 22.9911 6.21031H23ZM11.5066 17.0097C10.5105 15.0236 9.38152 13.7176 7.68142 11.9703C9.83311 11.4434 11.0241 9.41229 11.5066 7.91263C11.9892 9.41229 13.1802 11.4434 15.3319 11.9703C13.6318 13.7176 12.5028 15.0281 11.5066 17.0097Z" fill="#F2F0EF"/>
                </svg>
                <p className="font-nunito text-[#F2F0EF] text-[1.5rem] lg:text-[1.625rem] font-[300] leading-[100%] tracking-[0.495rem] lg:tracking-[0.53625rem] uppercase">
                  {label}
                </p>
              </div>
            )}

            {title && (
              <div className="text-center lg:text-left text-white prose-em:!font-[200] lg:leading-[100%] prose-headings:text-[5.34975rem] lg:prose-headings:text-[6rem] prose-headings:leading-[120%] lg:prose-headings:leading-[100%] prose-headings:mb-0 prose-p:mb-0">
                <PortableText value={title} components={richTextComponent} />
              </div>
            )}

            <form
              action={formAction}
              className="flex flex-col lg:flex-row gap-[2rem] w-full"
            >
              <input
                type="password"
                name="password"
                placeholder="Enter password."	
                aria-label="Password"
                autoComplete="off"
                className="flex-1 min-h-[6.375rem] rounded-[0.5rem] border border-white/[12%] bg-[#181818] p-[2rem] font-nunito text-[2rem] font-[300] text-offwhite placeholder:text-[#F2F0EF]/[72%] outline-none focus:border-white/30 transition-colors"
              />
              <button
                type="submit"
                disabled={pending}
                className="lg:min-w-[29.25rem] min-h-[6.375rem] rounded-[0.5rem] border border-white/[12%] bg-white/[8%] backdrop-blur-[1rem] w-full lg:w-auto lg:px-[4rem] py-[1.5rem] lg:py-[2.5rem] font-nunito text-[2rem] font-[300] text-offwhite transition-colors hover:bg-white/[16%] leading-none disabled:opacity-50 flex-shrink-0"
              >
                {pending ? '...' : submitLabel}
              </button>
            </form>

            {state.error && (
              <p className="font-nunito text-[1.625rem] font-[300] text-[#E0696A] leading-[120%] -mt-[1rem]">
                {errorMessage}
              </p>
            )}
          </div>

          {(waitlistText || waitlistButton?.cta) && (
            <div className="w-full flex flex-col lg:flex-row items-center lg:justify-between gap-[2rem]">
              {waitlistText && (
                <p className="prose-em:!font-newsreader uppercase font-[300] prose-em:!font-[200] text-offwhite text-[3.39175rem] lg:text-[4rem] flex-shrink-0">
                  Not a <em>waitlist member?</em>
                </p>
              )}
              <span className="flex-1 h-[0.0625rem] bg-white hidden sm:block" />
              {waitlistButton?.cta && (
                <CTAWrapper cta={waitlistButton.cta}>
                  <span className="font-nunito font-[500] text-offwhite text-[2rem] underline underline-offset-[0.4rem] decoration-white/40 hover:decoration-white transition-colors flex-shrink-0">
                    {waitlistButton.cta.title}
                  </span>
                </CTAWrapper>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
